import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { Eye, EyeOff } from "lucide-react";
import { firebaseAuth } from "@/shared/lib/firebase";
import { useAuth } from "@/shared/auth/useAuth";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Wordmark } from "@/shared/components/brand/Spark";
import { sanitizeRedirectPath } from "./safe-redirect";

interface StoredLocation {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
}
interface LocationState {
  from?: StoredLocation;
}

function composeReturnTarget(from: StoredLocation | undefined): string | undefined {
  if (!from) return undefined;
  const parts: string[] = [];
  if (typeof from.pathname === "string") parts.push(from.pathname);
  if (typeof from.search === "string") parts.push(from.search);
  if (typeof from.hash === "string") parts.push(from.hash);
  const composed = parts.join("");
  return composed.length > 0 ? composed : undefined;
}

function signInErrorMessage(code: string): string {
  switch (code) {
    case "auth/user-disabled":
      return "This account has been disabled. Contact an administrator.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a moment and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return "Sign-in failed. Check your email and password.";
  }
}

export function LoginPage() {
  const { status, user, rejection } = useAuth();
  const location = useLocation();
  const from = sanitizeRedirectPath(
    composeReturnTarget((location.state as LocationState | null)?.from),
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A rejection means backend verification bounced the session — drop out of
  // the verifying lock so the form + banner render again.
  useEffect(() => {
    if (rejection !== null) setVerifying(false);
  }, [rejection]);

  if (status === "authenticated" && user) {
    return <Navigate to={from} replace />;
  }

  if (status === "loading" || verifying) {
    return (
      <div
        data-testid="login-verifying"
        className="flex min-h-screen items-center justify-center text-ge-secondary"
      >
        Verifying your account…
      </div>
    );
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      // Firebase accepted the credentials; AuthProvider now verifies the Growth
      // account. Keep the screen locked until that resolves.
      setVerifying(true);
    } catch (err) {
      const code =
        err != null && typeof err === "object" && "code" in err
          ? String((err as { code: unknown }).code)
          : "";
      setError(signInErrorMessage(code));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-screen"
      className="grid min-h-screen grid-cols-1 bg-ge-app lg:grid-cols-2"
    >
      {/* Brand panel — dark, decorative, desktop only. */}
      <aside
        aria-hidden
        className="relative hidden flex-col justify-between overflow-hidden border-r border-ge-border-subtle bg-ge-sidebar p-12 lg:flex"
      >
        <Wordmark />
        <div className="relative z-10 max-w-md space-y-4">
          <p className="ge-eyebrow !text-ge-accent">SUPPLY ACQUISITION</p>
          <h2 className="font-sans text-[26px] font-bold tracking-[-0.3px] text-ge-primary">
            Point the engine at a market.
          </h2>
          <p className="font-sans text-[14.5px] leading-relaxed text-ge-secondary">
            Discover leads, screen and enrich them, then run measured outreach — the whole
            supply pipeline in one command center.
          </p>
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-ge-accent/10 blur-3xl"
        />
      </aside>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center lg:hidden">
            <Wordmark />
          </div>
          <header className="space-y-2">
            <p className="ge-eyebrow">SIGN IN</p>
            <h1 className="font-sans text-[24px] font-bold tracking-[-0.3px] text-ge-primary">
              Welcome back
            </h1>
            <p className="font-sans text-[14.5px] text-ge-secondary">
              Sign in with your Growth Engine account.
            </p>
          </header>

          {rejection === "not-provisioned" && (
            <div
              data-testid="login-not-provisioned"
              role="alert"
              className="rounded-ge-control border border-ge-border-danger-dashed bg-ge-danger/10 p-3 text-[13px] text-ge-danger"
            >
              Your identity is valid but has no Growth Engine account. Contact an administrator.
            </div>
          )}
          {rejection === "verification-failed" && (
            <div
              data-testid="login-verification-failed"
              role="alert"
              className="space-y-2 rounded-ge-control border border-ge-border-danger-dashed bg-ge-danger/10 p-3 text-[13px] text-ge-danger"
            >
              <p>We couldn&apos;t verify your account. This is usually temporary.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="verification-retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ge-muted hover:text-ge-value focus-visible:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-[16px] w-[16px]" strokeWidth={1.8} />
                  ) : (
                    <Eye className="h-[16px] w-[16px]" strokeWidth={1.8} />
                  )}
                </button>
              </div>
            </div>
            {error && (
              <p data-testid="login-error" className="text-[13px] text-ge-danger">
                {error}
              </p>
            )}
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={submitting}
              data-testid="sign-in-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
